import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { sendText, sendFile, sendImages } from "../shared/chatApi";
import { validateFile, validateImages, DEFAULTS, CHAT_FILE_ACCEPT_ATTR } from "./fileValidation";
import {
  CHAT_IMAGE_ACCEPT_ATTR,
  isChatImageFile,
  normalizeChatImagesForUpload,
} from './chatImageCompatibility';
import {
  buildMentionInsertion,
  buildOutgoingMentions,
  findMentionTrigger,
} from "../mentions/chatMentionUtils";
import ChatComposerView from "./ChatComposerView";
import useChatComposerSend from "./useChatComposerSend";
import useChatQuestionComposer from "../questions/useChatQuestionComposer";
import {
  CHAT_NOTIFICATION_DELAY_UNTIL_MORNING,
  CHAT_NOTIFICATION_IMMEDIATE,
  buildComposerHighlightNodes,
  isLateNightNotificationWindowJst,
  normalizeClipboardImageFile,
} from "./ChatComposerUtils";
import "../../../../css/common/chat/ChatComposer.css";
import "../../../../css/common/chat/ChatQuestions.css";
const ChatComposer = forwardRef(function ChatComposer(
  {
    mode = "admin",
    scope = "individual",
    artistId,
    eventId,
    groupThreadId,
    canSendFiles = true,
    placeholderText = "メッセージを入力…",
    onSent,
    onBeforeSend,
    onUploading,
    senderType,
    senderId,
    readOnly = false,
    onSendBlocked,
    blockedMessage,
    replyDraft = null,
    onReplyCancel,
    mentionCandidates = [],
    mentionHelperText = '',
    onMentionQueryChange,
    initialDraft = null,
  },
  ref,
) {
  const [text, setText] = useState("");
  const [sendingKind, setSendingKind] = useState(null); // "text" | "file" | "images" | null
  const [pendingImages, setPendingImages] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 640px)").matches
      : false,
  );
  const [mentionState, setMentionState] = useState(null);
  const [mentionActiveTab, setMentionActiveTab] = useState("person");
  // -1 = 未選択。矢印キーで移動するかホバーするまで、どの候補にも選択表示を出さない。
  const [mentionActiveIndex, setMentionActiveIndex] = useState(-1);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [lateNightPrompt, setLateNightPrompt] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const textareaRef = useRef(null);
  // キーボード時の持ち上げ量を window.innerHeight ではなく composer 実測底で決めるための ref。
  // getBoundingClientRect と visualViewport は同一座標系なので、Android の 100dvh と
  // innerHeight のナビバー扱いの差が相殺され、3ボタンナビでも入力欄がキーボード直上に来る。
  // composer 自身は transform されるため、測定値に現在の offset を足し戻して静止下端を復元する。
  const composerAnchorRef = useRef(null);
  const keyboardOffsetRef = useRef(0);
  keyboardOffsetRef.current = keyboardOffset;
  const mentionHighlightRef = useRef(null);
  const composerShellRef = useRef(null);
  const pendingImagesRef = useRef([]);
  const pendingImageSeqRef = useRef(0);
  const lateNightPromptResolverRef = useRef(null);
  const appliedInitialDraftKeyRef = useRef("");

  // 同一ページに複数設置されても label-for が衝突しないようにユニークIDを付与
  const uidRef = useRef(`cc-${Math.random().toString(36).slice(2, 10)}`);
  const imageInputId = `${uidRef.current}-chat-image-input`;
  const fileInputId = `${uidRef.current}-chat-file-input`;

  // 実際に使う送信者タイプ（senderType が来ていれば優先、無ければ mode から推論）
  const effectiveSenderType = senderType ?? (mode === "admin" ? "admin" : "artist");

  // 設定値
  const MAX_TEXT = DEFAULTS?.maxText ?? 5000;
  const MAX_IMAGES = DEFAULTS?.maxImages ?? 5;
  const BASE_TEXTAREA_PX = 42; // 初期表示は約2行分
  const MAX_TEXTAREA_PX = 156; // 入力欄の最大高さ(px)

  // 受け入れる MIME
  const acceptDocAttr = useMemo(
    () => DEFAULTS?.acceptFileMime?.join(",") || CHAT_FILE_ACCEPT_ATTR,
    [],
  );
  const acceptImageAttr = CHAT_IMAGE_ACCEPT_ATTR;

  useEffect(() => () => {
    if (lateNightPromptResolverRef.current) {
      lateNightPromptResolverRef.current(null);
      lateNightPromptResolverRef.current = null;
    }
  }, []);

  const closeLateNightPrompt = useCallback((deliveryMode) => {
    const resolver = lateNightPromptResolverRef.current;
    lateNightPromptResolverRef.current = null;
    setLateNightPrompt(null);
    if (resolver) resolver(deliveryMode);
  }, []);

  const resolveNotificationDeliveryMode = useCallback(() => {
    if (!isLateNightNotificationWindowJst()) {
      return Promise.resolve(CHAT_NOTIFICATION_IMMEDIATE);
    }
    if (lateNightPromptResolverRef.current) {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      lateNightPromptResolverRef.current = resolve;
      setLateNightPrompt({ openedAt: Date.now() });
    });
  }, []);

  const charCount = text.length;
  const textTooLong = charCount > MAX_TEXT;
  const normalizedMentionCandidates = useMemo(
    () => (Array.isArray(mentionCandidates) ? mentionCandidates : [])
      .filter((candidate) => candidate && candidate.id && candidate.label),
    [mentionCandidates],
  );
  const filteredMentionCandidateGroups = useMemo(() => {
    const empty = { person: [], part: [] };
    if (!mentionState) return empty;
    const query = String(mentionState.query || '').trim().toLowerCase();
    const matched = normalizedMentionCandidates.filter((candidate) => {
      const label = String(candidate.label || '').toLowerCase();
      const subtitle = String(candidate.subtitle || '').toLowerCase();
      return !query || label.includes(query) || subtitle.includes(query);
    });
    return matched.reduce((acc, candidate) => {
      const key = candidate.type === "part" ? "part" : "person";
      acc[key].push(candidate);
      return acc;
    }, empty);
  }, [mentionState, normalizedMentionCandidates]);
  const visibleMentionCandidates = filteredMentionCandidateGroups[mentionActiveTab] || [];
  const mentionPersonCount = filteredMentionCandidateGroups.person.length;
  const mentionPartCount = filteredMentionCandidateGroups.part.length;
  const hasMentionCandidates = mentionPersonCount + mentionPartCount > 0;
  const showMentionMenu = Boolean(mentionState && (hasMentionCandidates || mentionHelperText));
  const hasMultipleMentionTypes = mentionPersonCount > 0 && mentionPartCount > 0;
  const composerMentionRanges = useMemo(
    () => buildOutgoingMentions(selectedMentions, text)
      .map((mention) => ({
        start: Number.parseInt(mention.start, 10),
        end: Number.parseInt(mention.end, 10),
        label: mention.label,
      }))
      .filter((mention) =>
        Number.isInteger(mention.start) &&
        Number.isInteger(mention.end) &&
        mention.end > mention.start
      )
      .sort((a, b) => a.start - b.start),
    [selectedMentions, text],
  );
  const composerHighlightNodes = useMemo(
    () => buildComposerHighlightNodes(text, composerMentionRanges),
    [composerMentionRanges, text],
  );

  const pushError = (message) => {
    const list = Array.isArray(message) ? message : [message];
    setErrors((prev) => [...prev, ...list]);
  };
  const notifyBlocked = useCallback(() => {
    const msg = blockedMessage || "現在メンテナンス中のため送信できません。";
    pushError(msg);
    if (typeof onSendBlocked === "function") {
      onSendBlocked(msg);
    }
  }, [blockedMessage, onSendBlocked]);
  const clearErrors = () => setErrors([]);
  const revokePreviewUrl = useCallback((previewUrl) => {
    if (!previewUrl || typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function") return;
    try {
      URL.revokeObjectURL(previewUrl);
    } catch {
      // ignore
    }
  }, []);
  const revokePendingImageEntries = useCallback((entries) => {
    (entries || []).forEach((entry) => revokePreviewUrl(entry?.previewUrl || ""));
  }, [revokePreviewUrl]);
  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      revokePendingImageEntries(prev);
      return [];
    });
  }, [revokePendingImageEntries]);
  const removePendingImage = useCallback((entryId) => {
    setPendingImages((prev) => {
      const target = prev.find((entry) => entry.id === entryId);
      if (target) revokePreviewUrl(target.previewUrl);
      return prev.filter((entry) => entry.id !== entryId);
    });
  }, [revokePreviewUrl]);

  const createPendingImageEntry = useCallback((file, index = 0) => {
    const normalizedFile = normalizeClipboardImageFile(file, index);
    if (!normalizedFile) return null;
    const previewUrl =
      typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
        ? URL.createObjectURL(normalizedFile)
        : "";
    pendingImageSeqRef.current += 1;
    return {
      id: `clipboard-image-${pendingImageSeqRef.current}`,
      file: normalizedFile,
      previewUrl,
      displayName: normalizedFile.name || `clipboard-image-${pendingImageSeqRef.current}`,
    };
  }, []);

  // テキストエリアの高さを制御
  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    // 改行が1つも無い間は高さ固定（入力しても増やさない）
    if (!text.includes("\n")) {
      ta.style.height = `${BASE_TEXTAREA_PX}px`;
      ta.style.overflowY = "hidden";
      return;
    }

    // 改行が入ったら通常のオートリサイズ
    ta.style.height = "auto";
    const next = Math.min(ta.scrollHeight, MAX_TEXTAREA_PX);
    ta.style.height = `${next}px`;
    ta.style.overflowY = ta.scrollHeight > MAX_TEXTAREA_PX ? "auto" : "hidden";
  }, [text]);

  useEffect(() => {
    autoResize();
  }, [autoResize]);

  useEffect(() => {
    const key = String(initialDraft?.key || "").trim();
    if (!key || appliedInitialDraftKeyRef.current === key) return;
    const nextText = String(initialDraft?.text || "").trimStart();
    if (!nextText.trim()) return;
    const nextMentions = (Array.isArray(initialDraft?.mentions) ? initialDraft.mentions : [])
      .map((mention) => ({
        type: mention?.type === "part" ? "part" : "person",
        role: mention?.role || mention?.targetRole || "admin",
        id: String(mention?.id || mention?.targetId || mention?.target_id || ""),
        label: String(mention?.label || "").replace(/^@+/, "").trim(),
        start: Number.isInteger(Number(mention?.start)) ? Number(mention.start) : 0,
        end: Number.isInteger(Number(mention?.end)) ? Number(mention.end) : 0,
      }))
      .filter((mention) => mention.id && mention.label);
    appliedInitialDraftKeyRef.current = key;
    setText(nextText);
    setSelectedMentions(nextMentions);
    setMentionState(null);
    setErrors([]);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const cursor = nextText.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
      autoResize();
    });
  }, [autoResize, initialDraft]);

  useEffect(() => {
    setMentionActiveIndex(-1);
  }, [mentionActiveTab, mentionState?.start, mentionState?.query]);
  useEffect(() => {
    if (!mentionState) return;
    if (mentionActiveTab === "person" && mentionPersonCount > 0) return;
    if (mentionActiveTab === "part" && mentionPartCount > 0) return;
    if (mentionPersonCount > 0) {
      setMentionActiveTab("person");
      return;
    }
    if (mentionPartCount > 0) setMentionActiveTab("part");
  }, [mentionActiveTab, mentionPartCount, mentionPersonCount, mentionState]);
  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 640px)");
    const handler = (e) => {
      setIsCompact(e.matches);
    };
    handler(mql);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const handleVv = () => {
      // composer の静止下端（transform分を足し戻した実測底）と可視ビューポート下端の差＝
      // キーボード直上に置くための持ち上げ量。同一座標系なので 100dvh/innerHeight のナビバー
      // 扱いの差が自動相殺され、3ボタンナビでもナビバー高さ分沈まない。未マウント時は
      // innerHeight にフォールバック。
      const el = composerAnchorRef.current;
      const anchorBottom = el
        ? el.getBoundingClientRect().bottom + keyboardOffsetRef.current
        : window.innerHeight;
      const offset = Math.max(0, anchorBottom - (vv.offsetTop + vv.height));
      setKeyboardOffset(offset > 40 ? Math.min(offset, 360) : 0);
    };
    vv.addEventListener("resize", handleVv);
    vv.addEventListener("scroll", handleVv);
    handleVv();
    return () => {
      vv.removeEventListener("resize", handleVv);
      vv.removeEventListener("scroll", handleVv);
    };
  }, []);

  // メンテ解除・明示ブロック解除時にエラーを自動クリア
  useEffect(() => {
    if (!readOnly && errors.length > 0) {
      const timer = setTimeout(() => setErrors([]), 1200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [readOnly, errors.length]);
  useEffect(
    () => () => {
      revokePendingImageEntries(pendingImagesRef.current);
    },
    [revokePendingImageEntries],
  );

  const conversationKey = useMemo(
    () =>
      [
        mode,
        scope,
        artistId || "",
        eventId || "",
        groupThreadId || "",
        senderType || "",
        senderId || "",
      ].join(":"),
    [artistId, eventId, groupThreadId, mode, scope, senderId, senderType],
  );
  const previousConversationKeyRef = useRef(conversationKey);

  useEffect(() => {
    if (previousConversationKeyRef.current === conversationKey) return;
    previousConversationKeyRef.current = conversationKey;
    clearPendingImages();
    setSelectedMentions([]);
    setMentionState(null);
    setMentionActiveTab("person");
  }, [clearPendingImages, conversationKey]);

  // 送信後にフォーカスを戻すためのヘルパー
  const focusTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    const len = ta.value.length;
    try {
      ta.setSelectionRange(len, len);
    } catch {
      // ignore
    }
  };

  useImperativeHandle(ref, () => ({
    focus: focusTextarea,
    getTextarea: () => textareaRef.current,
  }));

  const updateMentionTrigger = useCallback((nextText, cursorIndex) => {
    const trigger = findMentionTrigger(nextText, cursorIndex);
    setMentionState(trigger);
  }, []);

  useEffect(() => {
    if (typeof onMentionQueryChange !== "function") return;
    onMentionQueryChange(mentionState?.query || "", mentionState);
  }, [mentionState, onMentionQueryChange]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !text) {
      setMentionState(null);
      return;
    }
    updateMentionTrigger(text, textarea.selectionStart ?? text.length);
  }, [normalizedMentionCandidates.length, text, updateMentionTrigger]);

  const handleTextChange = useCallback((event) => {
    const nextText = event.target.value;
    const cursorIndex = event.target.selectionStart ?? nextText.length;
    setText(nextText);
    updateMentionTrigger(nextText, cursorIndex);
  }, [updateMentionTrigger]);

  const syncMentionHighlightScroll = useCallback((event) => {
    const layer = mentionHighlightRef.current;
    if (!layer) return;
    layer.scrollTop = event.currentTarget.scrollTop;
    layer.scrollLeft = event.currentTarget.scrollLeft;
  }, []);

  const insertMentionCandidate = useCallback((candidate) => {
    if (!candidate || !mentionState) return;
    const ta = textareaRef.current;
    const insertionResult = buildMentionInsertion({
      text,
      mentionState,
      selection: {
        start: ta?.selectionStart ?? mentionState.end,
        end: ta?.selectionEnd ?? ta?.selectionStart ?? mentionState.end,
      },
      label: candidate.label,
    });
    if (!insertionResult) return;
    const { mention, text: nextText, selection } = insertionResult;

    setText(nextText);
    setSelectedMentions((prev) => [
      ...prev,
      {
        type: candidate.type === 'part' ? 'part' : 'person',
        role: candidate.role || (candidate.type === 'part' ? 'artist' : ''),
        id: String(candidate.id),
        label: mention.label,
        start: mention.start,
        end: mention.end,
      },
    ]);
    setMentionState(null);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const nextCursor = selection.start;
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
      autoResize();
    });
  }, [autoResize, mentionState, text]);

  // 送信（テキスト）
  const submitTextMessage = useCallback(async (content, options = {}) => {
    const trimmed = String(content || "").trim();
    if (!trimmed) return;
    const mentions = buildOutgoingMentions(selectedMentions, trimmed);
    if (!options.skipBeforeSend && typeof onBeforeSend === "function") {
      const shouldSend = await onBeforeSend({
        type: "text",
        text: trimmed,
        mentions,
        scope,
        eventId,
        groupThreadId,
        senderType: effectiveSenderType,
      });
      if (shouldSend === false) return;
    }
    const notificationDeliveryMode = options.notificationDeliveryMode || await resolveNotificationDeliveryMode();
    if (!notificationDeliveryMode) return;
    try {
      setSendingKind("text");
      // ロギング/将来の拡張用に明示的に通知（BusyOverlay は出さない）
      onUploading && onUploading({ active: false, kind: "text" });
      await sendText({
        senderType: effectiveSenderType,
        senderId,
        mode: effectiveSenderType, // 後方互換: chatApi 側が mode を見ている場合にも一致させる
        scope,
        eventId,
        groupThreadId,
        content: trimmed,
        mentions,
        notificationDeliveryMode,
        clientMessageId: options.clientMessageId,
        ...(scope === "individual" && effectiveSenderType === "admin" && artistId
          ? { artistId }
          : {}),
        replyToMessageId: replyDraft?.messageId || "",
      });
      if (typeof onSent === "function") await onSent({ type: "text", text: trimmed });
    } finally {
      setSendingKind(null);
    }
  }, [
    artistId,
    effectiveSenderType,
    eventId,
    groupThreadId,
    onBeforeSend,
    onSent,
    onUploading,
    resolveNotificationDeliveryMode,
    replyDraft?.messageId,
    scope,
    selectedMentions,
    senderId,
  ]);

  // 送信（ドキュメント系ファイル）
  const doSendFile = async (file) => {
    if (!canSendFiles) return;
    clearErrors();
    if (readOnly) {
      notifyBlocked();
      return;
    }
    const { ok, errors: validationErrors = [] } = validateFile(file || null);
    if (validationErrors.length) {
      pushError(validationErrors);
      return;
    }
    try {
      const notificationDeliveryMode = await resolveNotificationDeliveryMode();
      if (!notificationDeliveryMode) return;
      setSendingKind("file");
      onUploading && onUploading({ active: true, kind: "file" });
      await sendFile({
        senderType: effectiveSenderType,
        senderId,
        mode: effectiveSenderType,
        scope,
        eventId,
        groupThreadId,
        file: ok || file,
        extraFields:
          scope === "individual" && effectiveSenderType === "admin" && artistId
            ? { artistId }
            : undefined,
        replyToMessageId: replyDraft?.messageId || "",
        notificationDeliveryMode,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (typeof onSent === "function") await onSent({ type: "file" });
    } catch (err) {
      pushError(err?.message || "ファイルの送信に失敗しました。");
    } finally {
      setSendingKind(null);
      onUploading && onUploading({ active: false, kind: "file", count: 1 });
      requestAnimationFrame(focusTextarea);
    }
  };

  // 送信（画像：最大5枚）
  const submitImagesMessage = useCallback(async (filesLike, options = {}) => {
    let files = Array.from(filesLike || []).filter(Boolean);
    if (files.length === 0) return;
    const notificationDeliveryMode = options.notificationDeliveryMode || await resolveNotificationDeliveryMode();
    if (!notificationDeliveryMode) return;
    setSendingKind("images");
    onUploading && onUploading({ active: true, kind: "images" });
    try {
      await sendImages({
        senderType: effectiveSenderType,
        senderId,
        mode: effectiveSenderType,
        scope,
        eventId,
        groupThreadId,
        files,
        extraFields:
          scope === "individual" && effectiveSenderType === "admin" && artistId
            ? { artistId }
            : undefined,
        replyToMessageId: replyDraft?.messageId || "",
        notificationDeliveryMode,
      });
      if (typeof onSent === "function") await onSent({ type: "images" });
    } finally {
      setSendingKind(null);
      onUploading && onUploading({ active: false, kind: "images", count: files.length });
    }
  }, [
    artistId,
    effectiveSenderType,
    eventId,
    groupThreadId,
    onSent,
    onUploading,
    resolveNotificationDeliveryMode,
    replyDraft?.messageId,
    scope,
    senderId,
  ]);

  const doSendImages = async (filesLike) => {
    if (!canSendFiles) return;
    clearErrors();
    if (readOnly) {
      notifyBlocked();
      return;
    }

    const files = Array.from(filesLike || []).filter(Boolean);
    if (files.length === 0) return;

    if (files.length > 5) {
      pushError("画像は一度に5枚まで送信できます。");
      return;
    }
    const invalid = files.find((file) => !isChatImageFile(file));
    if (invalid) {
      pushError("送信できる画像は JPEG / PNG / WebP / AVIF / HEIC / HEIF です。");
      return;
    }

    try {
      const compatibleFiles = await normalizeChatImagesForUpload(files);
      await submitImagesMessage(compatibleFiles);
      if (imageInputRef.current) imageInputRef.current.value = "";
    } catch (err) {
      pushError(err?.message || "画像の送信に失敗しました。");
    } finally {
      requestAnimationFrame(focusTextarea);
    }
  };

  const queueClipboardImages = useCallback((files) => {
    const availableSlots = Math.max(0, MAX_IMAGES - pendingImagesRef.current.length);
    if (availableSlots <= 0) {
      pushError(`画像は最大 ${MAX_IMAGES} 枚まで送信待機できます。`);
      return;
    }
    const { ok, errors: validationErrors = [] } = validateImages(files, { maxImages: availableSlots });
    if (validationErrors.length) pushError(validationErrors);
    if (!ok.length) return;
    const entries = ok
      .map((file, index) => createPendingImageEntry(file, pendingImagesRef.current.length + index))
      .filter(Boolean);
    if (!entries.length) return;
    setPendingImages((prev) => [...prev, ...entries]);
  }, [MAX_IMAGES, createPendingImageEntry]);

  const extractClipboardImageFiles = useCallback((clipboardData) => {
    const items = Array.from(clipboardData?.items || []);
    const itemFiles = items
      .filter((item) => item?.kind === "file" && /^image\//i.test(String(item?.type || "")))
      .map((item) => item.getAsFile?.())
      .filter(Boolean);
    if (itemFiles.length) return itemFiles;
    return Array.from(clipboardData?.files || []).filter((file) =>
      /^image\//i.test(String(file?.type || "")),
    );
  }, []);

  const handlePaste = useCallback((event) => {
    const imageFiles = extractClipboardImageFiles(event?.clipboardData);
    if (!imageFiles.length) return;
    event.preventDefault();
    clearErrors();
    if (!canSendFiles) {
      pushError("現在このチャットでは画像を送信できません。");
      return;
    }
    if (readOnly) {
      notifyBlocked();
      return;
    }
    queueClipboardImages(imageFiles);
  }, [canSendFiles, extractClipboardImageFiles, notifyBlocked, queueClipboardImages, readOnly]);

  const handleSend = useChatComposerSend({
    maxText: MAX_TEXT,
    text,
    selectedMentions,
    pendingImagesRef,
    readOnly,
    notifyBlocked,
    onBeforeSend,
    scope,
    eventId,
    groupThreadId,
    effectiveSenderType,
    resolveNotificationDeliveryMode,
    submitImagesMessage,
    submitTextMessage,
    clearPendingImages,
    setText,
    setSelectedMentions,
    setMentionState,
    pushError,
    focusTextarea,
    clearErrors,
  });

  // キー操作：Enter=改行, Alt/⌥/⌘ + Enter=送信
  const handleKeyDown = (event) => {
    if (mentionState && visibleMentionCandidates.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionActiveIndex((prev) =>
          (prev < 0 ? 0 : (prev + 1) % visibleMentionCandidates.length),
        );
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionActiveIndex((prev) =>
          (prev < 0
            ? visibleMentionCandidates.length - 1
            : (prev - 1 + visibleMentionCandidates.length) % visibleMentionCandidates.length),
        );
        return;
      }
      // 候補を上下キーで選んでいるときだけ Enter/Tab で確定する。
      // 未選択（mentionActiveIndex < 0）のときは通常の改行／送信へ委ねる。
      if (event.key === "Enter" || event.key === "Tab") {
        const activeCandidate = visibleMentionCandidates[mentionActiveIndex];
        if (activeCandidate) {
          event.preventDefault();
          insertMentionCandidate(activeCandidate);
          return;
        }
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMentionState(null);
        return;
      }
    }
    if (event.key === "Enter" && (event.altKey || event.metaKey)) {
      event.preventDefault();
      if (!sendingKind) handleSend();
    }
  };

  const questionComposer = useChatQuestionComposer({ enabled: effectiveSenderType === "admin", readOnly, notifyBlocked, clearErrors, initialSupplementalMessage: text, scope, artistId, eventId, groupThreadId, senderType: effectiveSenderType, onBeforeSend, onSent, onQuestionSent: () => { setText(""); setSelectedMentions([]); setMentionState(null); } });
  return <><ChatComposerView {...{ keyboardOffset, composerAnchorRef, replyDraft, onReplyCancel, errors, pendingImages, MAX_IMAGES, clearPendingImages, removePendingImage, imageInputRef, acceptImageAttr, doSendImages, imageInputId, canSendFiles, sendingKind, readOnly, fileInputRef, acceptDocAttr, doSendFile, fileInputId, showMentionMenu, mentionHelperText, hasMultipleMentionTypes, mentionActiveTab, setMentionActiveTab, mentionPersonCount, mentionPartCount, visibleMentionCandidates, mentionActiveIndex, insertMentionCandidate, isCompact, composerShellRef, composerMentionRanges, mentionHighlightRef, composerHighlightNodes, textareaRef, placeholderText, text, handleTextChange, handlePaste, handleKeyDown, syncMentionHighlightScroll, updateMentionTrigger, notifyBlocked, MAX_TEXTAREA_PX, textTooLong, handleSend, lateNightPrompt, closeLateNightPrompt, uidRef, CHAT_NOTIFICATION_IMMEDIATE, CHAT_NOTIFICATION_DELAY_UNTIL_MORNING, canCreateQuestion: questionComposer.enabled, openQuestionModal: questionComposer.open }} />{questionComposer.node}</>;
});

export default ChatComposer;
